// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Hook for fetching SRM requests from the dedicated requests API,
 * with documents fetched in parallel for track counts.
 */

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { NodeApiClient } from '@/app-shell/services'
import { useDocuments } from '@/subsystems/documents'
import type { SrmRequest } from '../types'
import { listRequests } from '../services/api'
import { groupDocumentsByGroupId } from '../utils/grouping'

interface UseRequestsParams {
  enabled?: boolean
}

/**
 * Fetch SRM requests and documents, merge them for display.
 * Requests provide identity (survive zero docs), documents provide track counts.
 */
export function useRequests(
  apiClient: NodeApiClient,
  orgId: string,
  wsId: string,
  params?: UseRequestsParams,
) {
  const {
    data: requestListData,
    isLoading: requestsLoading,
    isError: requestsError,
    error: requestsErrorObj,
    refetch: refetchRequests,
  } = useQuery({
    queryKey: ['srm-requests', orgId, wsId],
    queryFn: () => listRequests(apiClient, orgId, wsId),
    enabled: params?.enabled,
  })

  const { documents, isLoading: docsLoading, refetch: refetchDocs } = useDocuments(
    apiClient,
    orgId,
    wsId,
    {
      enabled: params?.enabled,
      includePipelineStatus: true,
    },
  )

  const requests: SrmRequest[] = useMemo(() => {
    const apiRequests = requestListData?.requests ?? []
    if (apiRequests.length === 0) return []

    const docsByGroup = groupDocumentsByGroupId(documents)

    return apiRequests.map((req) => ({
      groupId: req.groupId,
      title: req.title,
      externalRef: req.externalRef,
      uploadedBy: req.createdBy,
      documents: docsByGroup.get(req.groupId) ?? [],
      createdAt: req.createdAt,
      updatedAt: req.updatedAt,
    }))
  }, [requestListData, documents])

  const refetch = () => {
    refetchRequests()
    refetchDocs()
  }

  return {
    requests,
    isLoading: requestsLoading || docsLoading,
    isError: requestsError,
    error: requestsErrorObj,
    refetch,
  }
}
